<script>
    import { page } from "$app/stores";
    import {
        blogPosts,
        getBlogImage,
        getBlogImageAbsolute,
    } from "$lib/data/blogPosts";
    import CodeBlock from "$lib/components/CodeBlock.svelte";
    import CursorTrail from "$lib/components/CursorTrail.svelte";
    import CelestialSky from "$lib/components/CelestialSky.svelte";
    import ReadingProgress from "$lib/components/ReadingProgress.svelte";
    import "$lib/styles/prism-dark.css";

    const genericCaniuseFeature = "es6-module";
    const INTEROP_TAG = "Interop 2026";

    function hasInteropTag(post) {
        return post?.tags.includes(INTEROP_TAG) ?? false;
    }

    function getDisplayTags(post) {
        return post?.tags.filter((tag) => tag !== INTEROP_TAG) ?? [];
    }
    const caniuseFeatureOverridesBySlug = Object.freeze({
        abortcontroller: "abortcontroller",
        "code-splitting-strategies": "es6-module-dynamic-import",
        "dynamic-import-chunking": "es6-module-dynamic-import",
        "module-federation": "es6-module-dynamic-import",
        "tree-shaking-internals": "es6-module-dynamic-import",
        indexeddb: "indexeddb",
        "cors-preflight": "cors",
        "trusted-types": "trusted-types",
        "http-3-and-quic": "http3",
        webrtc: "rtcpeerconnection",
    });

    function resolveCaniuseSupport(post) {
        if (!post) {
            return null;
        }

        const overrideFeature = caniuseFeatureOverridesBySlug[post.slug];
        const useOverride =
            Boolean(overrideFeature) &&
            post.caniuseFeature === genericCaniuseFeature;
        const feature = useOverride ? overrideFeature : post.caniuseFeature;

        // Skip generic placeholders for topics that don't map cleanly to a
        // single browser feature in Can I Use.
        if (!feature || feature === genericCaniuseFeature) {
            return null;
        }

        const embedFeature = post.caniuseEmbedFeature ?? feature;

        return {
            feature,
            embedFeature,
            usesFallback:
                Boolean(post.caniuseEmbedFeature) &&
                post.caniuseEmbedFeature !== feature,
            inferredFromTopic: useOverride,
        };
    }

    // Find the matching blog post based on the current URL
    $: slug = $page.url.pathname.split("/").pop();
    $: post = blogPosts.find((p) => p.slug === slug);
    $: caniuseSupport =
        post?.hideCaniuseSupport === true ? null : resolveCaniuseSupport(post);
    $: supportFeature = caniuseSupport?.feature;
    $: supportEmbedFeature = caniuseSupport?.embedFeature;
    $: supportFeatureUsesFallback = caniuseSupport?.usesFallback;
    $: supportFeatureWasInferred = caniuseSupport?.inferredFromTopic;
    $: displayTags = getDisplayTags(post);

    // Sort posts by recency (same order as the blog listing page)
    const postOrderBySlug = new Map(
        blogPosts.map((post, index) => [post.slug, index]),
    );
    const sortedPosts = [...blogPosts].sort((left, right) => {
        const dateDelta = right.date.localeCompare(left.date);
        if (dateDelta !== 0) return dateDelta;
        const leftOrder = postOrderBySlug.get(left.slug) ?? 0;
        const rightOrder = postOrderBySlug.get(right.slug) ?? 0;
        return rightOrder - leftOrder;
    });

    // Find the next post in sorted order
    $: currentIndex = sortedPosts.findIndex((p) => p.slug === slug);
    $: nextPost =
        currentIndex >= 0 && currentIndex < sortedPosts.length - 1
            ? sortedPosts[currentIndex + 1]
            : null;

    const caniusePeriods = "future_1,current,past_1,past_2";
    const caniuseAccessibleColours = "false";

    function getCaniuseEmbedUrl(feature) {
        const params = new URLSearchParams({
            feat: feature,
            periods: caniusePeriods,
            "accessible-colours": caniuseAccessibleColours,
        });
        return `https://caniuse.bitsofco.de/embed/index.html?${params.toString()}`;
    }

    function getCaniuseImageUrl(feature, format) {
        return `https://caniuse.bitsofco.de/image/${encodeURIComponent(feature)}.${format}`;
    }
</script>

<CodeBlock />

<svelte:head>
    {#if post}
        <title>{post.title} | dinglandia</title>
        <meta name="description" content={post.description} />
        <link
            rel="canonical"
            href="https://dinglandia.design/posts/{post.slug}"
        />

        <!-- Open Graph / Facebook -->
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="dinglandia" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta
            property="og:url"
            content="https://dinglandia.design/posts/{post.slug}"
        />
        <meta property="og:image" content={getBlogImageAbsolute(post.slug)} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={post.title} />
        <meta property="og:locale" content="en_US" />

        <!-- Article specific -->
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author} />
        {#each post.tags as tag}
            <meta property="article:tag" content={tag} />
        {/each}

        <!-- Twitter -->
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@dinglestein" />
        <meta name="twitter:creator" content="@dinglestein" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.description} />
        <meta name="twitter:image" content={getBlogImageAbsolute(post.slug)} />
        <meta name="twitter:image:alt" content={post.title} />
    {:else}
        <title>Post Not Found | dinglandia</title>
    {/if}
</svelte:head>

{#if post}
    <ReadingProgress totalReadTime={post.readTime} />

    <!-- Intro Section -->
    <section id="intro" class="intro" data-trail="wrapper">
        <div class="icon-new" name="logo"></div>
        <h1 class="flash">{post.title}</h1>
        <CursorTrail />
    </section>

    <article class="blog-post">
        <div class="blog-post__header">
            <a href="/blog" class="back-link">
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M12 4L6 10L12 16"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
                Back to Blog
            </a>

            {#if hasInteropTag(post)}
                <a
                    href="/blog?tag={encodeURIComponent(INTEROP_TAG)}"
                    class="interop-badge interop-badge--article">Interop 2026</a
                >
            {/if}

            <div class="blog-post__meta">
                <time datetime={post.date}>
                    {new Date(post.date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                    })}
                </time>
                <span class="separator">•</span>
                <span class="read-time">
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                    >
                        <circle
                            cx="8"
                            cy="8"
                            r="7"
                            stroke="currentColor"
                            stroke-width="1.5"
                        />
                        <path
                            d="M8 4V8L11 10"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                        />
                    </svg>
                    {post.readTime}
                </span>
                <span class="separator">•</span>
                <span
                    >By <a
                        href={post.authorLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="author-link">{post.author}</a
                    ></span
                >
            </div>

            {#if displayTags.length > 0}
                <div class="blog-post__tags">
                    {#each displayTags as tag}
                        <a
                            href="/blog?tag={encodeURIComponent(tag)}"
                            class="tag tag-link">{tag}</a
                        >
                    {/each}
                </div>
            {/if}
        </div>

        <div class="blog-post__image">
            <img src={getBlogImage(post.slug)} alt={post.title} />
        </div>

        <div class="blog-post__content">
            <slot />
        </div>

        {#if caniuseSupport}
            <section
                class="blog-post__support"
                aria-label="Browser support data"
            >
                <h2 class="support-title">Browser support snapshot</h2>
                <p class="support-copy">
                    Live support matrix for <code>{supportFeature}</code> from
                    Can I Use.
                    {#if supportFeatureWasInferred}
                        <span class="support-copy-note">
                            This chart is auto-mapped from the post topic
                            because the original metadata used a generic
                            placeholder.
                        </span>
                    {/if}
                    {#if supportFeatureUsesFallback}
                        <span class="support-copy-note">
                            Can I Use Embed does not currently expose
                            <code>{supportFeature}</code> directly, so this
                            chart uses
                            <code>{supportEmbedFeature}</code> as the closest available
                            proxy.
                        </span>
                    {/if}
                </p>

                <div class="support-embed-wrap">
                    <iframe
                        src={getCaniuseEmbedUrl(supportEmbedFeature)}
                        class="support-embed"
                        title={`Browser support for ${post.title}`}
                        loading="lazy"
                        referrerpolicy="strict-origin-when-cross-origin"
                    ></iframe>
                </div>

                <details class="support-fallback">
                    <summary>Show static fallback image</summary>
                    <picture>
                        <source
                            type="image/webp"
                            srcset={getCaniuseImageUrl(
                                supportEmbedFeature,
                                "webp",
                            )}
                        />
                        <source
                            type="image/png"
                            srcset={getCaniuseImageUrl(
                                supportEmbedFeature,
                                "png",
                            )}
                        />
                        <img
                            src={getCaniuseImageUrl(supportEmbedFeature, "jpg")}
                            alt={`Data on support for ${supportEmbedFeature} across major browsers from caniuse.com`}
                            loading="lazy"
                        />
                    </picture>
                </details>

                <p class="support-source">
                    Source:
                    <a
                        href={`https://caniuse.com/?search=${encodeURIComponent(supportFeature)}`}
                        target="_blank"
                        rel="noopener noreferrer">caniuse.com</a
                    >
                </p>
            </section>

            {#if post.caniuseFeature2}
                <section
                    class="blog-post__support"
                    aria-label="Browser support data for {post.caniuseFeature2}"
                >
                    <h2 class="support-title">Browser support snapshot</h2>
                    <p class="support-copy">
                        Live support matrix for <code
                            >{post.caniuseFeature2}</code
                        > from Can I Use.
                    </p>

                    <div class="support-embed-wrap">
                        <iframe
                            src={getCaniuseEmbedUrl(post.caniuseFeature2)}
                            class="support-embed"
                            title={`Browser support for ${post.caniuseFeature2}`}
                            loading="lazy"
                            referrerpolicy="strict-origin-when-cross-origin"
                        ></iframe>
                    </div>

                    <details class="support-fallback">
                        <summary>Show static fallback image</summary>
                        <picture>
                            <source
                                type="image/webp"
                                srcset={getCaniuseImageUrl(
                                    post.caniuseFeature2,
                                    "webp",
                                )}
                            />
                            <source
                                type="image/png"
                                srcset={getCaniuseImageUrl(
                                    post.caniuseFeature2,
                                    "png",
                                )}
                            />
                            <img
                                src={getCaniuseImageUrl(
                                    post.caniuseFeature2,
                                    "jpg",
                                )}
                                alt={`Data on support for ${post.caniuseFeature2} across major browsers from caniuse.com`}
                                loading="lazy"
                            />
                        </picture>
                    </details>

                    <p class="support-source">
                        Source:
                        <a
                            href={`https://caniuse.com/?search=${encodeURIComponent(post.caniuseFeature2)}`}
                            target="_blank"
                            rel="noopener noreferrer">caniuse.com</a
                        >
                    </p>
                </section>
            {/if}
        {/if}

        {#if displayTags.length > 0}
            <section class="blog-post__tag-filters" aria-label="Post tags">
                <h2 class="tag-filters__title">Explore by tag</h2>
                <div class="filter-tags">
                    {#each displayTags as tag}
                        <a
                            href="/blog?tag={encodeURIComponent(tag)}"
                            class="filter-tag">#{tag}</a
                        >
                    {/each}
                </div>
            </section>
        {/if}

        <footer class="blog-post__footer">
            <a href="/blog" class="btn-back">← Back to all posts</a>
            {#if nextPost}
                <a href="/posts/{nextPost.slug}" class="btn-next"
                    >Read next post →</a
                >
            {/if}
        </footer>
    </article>

    <!-- New Mountain Parallax -->
    <main class="content-grid">
        <section class="breakout">
            <CelestialSky />
            <div
                class="frame frame-square parallax-y"
                role="img"
                aria-label="Multi-layered scene depicting an underwater scene of some fish and Manta Ray. Set within the outline of a Manta Ray."
                style="--multiplier: 4"
            >
                <img
                    src="/img/mountains/mtnBlu5.svg"
                    alt=""
                    style="--offset:0.1"
                />
                <img
                    src="/img/mountains/mtnBlu4.svg"
                    alt=""
                    style="--offset:0.075"
                />
                <img
                    src="/img/mountains/mtnBlue3.svg"
                    alt=""
                    style="--offset:0.05"
                />
                <img
                    src="/img/mountains/mtnBlu2_fix.svg"
                    alt=""
                    style="--offset:0.025"
                />
                <img
                    src="/img/mountains/mtnBlue.svg"
                    alt=""
                    style="--offset:0.012"
                />
                <div class="mountain-base"></div>
            </div>
            <p class="supports"></p>
        </section>
    </main>
    <!-- //New Mountain Parallax -->

    <!-- Services -->
    <section id="services" class="services">
        <div class="container">
            <h2 class="text-center">What We Do</h2>
            <div class="services-grid">
                <a href="/services#v-pills-discover">
                    <svg class="icon icon-discover"
                        ><use xlink:href="#icon-discover"></use></svg
                    >
                    <p>Discover</p>
                </a>
                <a href="/services#v-pills-design">
                    <svg class="icon icon-design"
                        ><use xlink:href="#icon-design"></use></svg
                    >
                    <p>Design</p>
                </a>
                <a href="/services#v-pills-develop">
                    <svg class="icon icon-develop"
                        ><use xlink:href="#icon-develop"></use></svg
                    >
                    <p>Develop</p>
                </a>
                <a href="/services#v-pills-devops">
                    <svg class="icon icon-devops"
                        ><use xlink:href="#icon-devops"></use></svg
                    >
                    <p>DevOps</p>
                </a>
                <a href="/services#v-pills-agentic">
                    <svg class="icon icon-robot"
                        ><use xlink:href="#icon-robot"></use></svg
                    >
                    <p>Agentic</p>
                </a>
            </div>

            <div class="row">
                <div class="col-md-6 offset-md-3">
                    <div class="d-grid gap-2 col-6 mx-auto">
                        <a
                            href="/work"
                            class="btn btn-lg btn-custom"
                            type="button">view work</a
                        >
                    </div>
                </div>
            </div>
        </div>
    </section>
    <!-- //Services -->

    <!-- Water Ripple CTA -->
    <section class="ripple-cta">
        <div class="water-container">
            <canvas id="pool" aria-hidden="true"></canvas>
        </div>

        <div class="viking-ship-container">
            <svg
                class="viking-ship"
                viewBox="0 0 200 100"
                xmlns="http://www.w3.org/2000/svg"
                aria-label="Viking ship sailing"
            >
                <!-- Ship Hull -->
                <path
                    d="M 20 50 Q 10 60, 20 70 L 180 70 Q 190 60, 180 50 Z"
                    fill="#4a3426"
                    stroke="#2a1f1a"
                    stroke-width="2"
                />

                <!-- Hull Details -->
                <rect
                    x="25"
                    y="55"
                    width="150"
                    height="3"
                    fill="#3a2920"
                    opacity="0.7"
                />
                <rect
                    x="25"
                    y="62"
                    width="150"
                    height="2"
                    fill="#3a2920"
                    opacity="0.5"
                />

                <!-- Mast -->
                <rect x="98" y="15" width="4" height="45" fill="#2a1f1a" />

                <!-- Sail -->
                <path
                    d="M 45 20 Q 100 25, 155 20 L 150 45 Q 100 48, 50 45 Z"
                    fill="#e8d5c4"
                    stroke="#2a1f1a"
                    stroke-width="1.5"
                    opacity="0.95"
                />

                <!-- Sail Stripes -->
                <rect
                    x="50"
                    y="25"
                    width="100"
                    height="4"
                    fill="#b84c4c"
                    opacity="0.8"
                />
                <rect
                    x="50"
                    y="33"
                    width="100"
                    height="4"
                    fill="#b84c4c"
                    opacity="0.8"
                />
                <rect
                    x="50"
                    y="41"
                    width="100"
                    height="3"
                    fill="#b84c4c"
                    opacity="0.8"
                />

                <!-- Shield decorations -->
                <circle
                    cx="40"
                    cy="60"
                    r="6"
                    fill="#b84c4c"
                    stroke="#2a1f1a"
                    stroke-width="1"
                />
                <circle
                    cx="60"
                    cy="60"
                    r="6"
                    fill="#4a7299"
                    stroke="#2a1f1a"
                    stroke-width="1"
                />
                <circle
                    cx="80"
                    cy="60"
                    r="6"
                    fill="#b84c4c"
                    stroke="#2a1f1a"
                    stroke-width="1"
                />
                <circle
                    cx="100"
                    cy="60"
                    r="6"
                    fill="#4a7299"
                    stroke="#2a1f1a"
                    stroke-width="1"
                />
                <circle
                    cx="120"
                    cy="60"
                    r="6"
                    fill="#b84c4c"
                    stroke="#2a1f1a"
                    stroke-width="1"
                />
                <circle
                    cx="140"
                    cy="60"
                    r="6"
                    fill="#4a7299"
                    stroke="#2a1f1a"
                    stroke-width="1"
                />
                <circle
                    cx="160"
                    cy="60"
                    r="6"
                    fill="#b84c4c"
                    stroke="#2a1f1a"
                    stroke-width="1"
                />

                <!-- Dragon head -->
                <path
                    d="M 180 50 Q 195 45, 197 50 L 195 55 Q 190 52, 180 52 Z"
                    fill="#4a3426"
                    stroke="#2a1f1a"
                    stroke-width="1.5"
                />

                <!-- Oars (simplified) -->
                <line
                    x1="35"
                    y1="68"
                    x2="28"
                    y2="78"
                    stroke="#2a1f1a"
                    stroke-width="2"
                    stroke-linecap="round"
                />
                <line
                    x1="55"
                    y1="68"
                    x2="48"
                    y2="78"
                    stroke="#2a1f1a"
                    stroke-width="2"
                    stroke-linecap="round"
                />
                <line
                    x1="75"
                    y1="68"
                    x2="68"
                    y2="78"
                    stroke="#2a1f1a"
                    stroke-width="2"
                    stroke-linecap="round"
                />
                <line
                    x1="125"
                    y1="68"
                    x2="132"
                    y2="78"
                    stroke="#2a1f1a"
                    stroke-width="2"
                    stroke-linecap="round"
                />
                <line
                    x1="145"
                    y1="68"
                    x2="152"
                    y2="78"
                    stroke="#2a1f1a"
                    stroke-width="2"
                    stroke-linecap="round"
                />
                <line
                    x1="165"
                    y1="68"
                    x2="172"
                    y2="78"
                    stroke="#2a1f1a"
                    stroke-width="2"
                    stroke-linecap="round"
                />
            </svg>
        </div>

        <div class="trees trees--cta" aria-hidden="true"></div>

        <div class="overlay" aria-hidden="true">
            <div class="overlay-inner">
                <h2 class="title">We take you from point A to point B.</h2>
                <h3 class="subtitle">And everywhere in-between.</h3>
                <div class="cta">
                    Let's create custom web experiences for your customers!
                </div>
                <div class="logo-tag">
                    <button
                        type="button"
                        class="btn btn-lg btn-custom"
                        data-fillout-id="n2vCxyr9Huus"
                        data-fillout-embed-type="popup"
                        data-fillout-dynamic-resize
                        data-fillout-inherit-parameters
                        data-fillout-popup-size="small"
                        >navigate the winds</button
                    >
                </div>
            </div>
        </div>

        <div class="fallback" hidden aria-hidden="true">
            WebGL2 is required to view this water effect.<br />
            Please try again in a modern browser.
        </div>
    </section>
    <!-- Water Ripple CTA -->
{:else}
    <div class="error-page">
        <h1>Post not found</h1>
        <a href="/blog">← Back to Blog</a>
    </div>
{/if}

<style>
    .intro h1 {
        font-size: clamp(7svw, 3vw + 1rem, 14svw);
        line-height: 1.2;
    }

    /*:global(html),
    :global(body) {
        color-scheme: light only !important;
        background-color: #f5f5f0 !important;
        color: #333333 !important;
    }*/

    .intro-label {
        font-size: clamp(2rem, 5vw, 3rem);
        font-weight: 700;
        text-align: center;
        margin: 0;
        color: var(--text);
    }

    .blog-post {
        max-width: 800px;
        margin: 0 auto;
        padding: 2rem 1rem;
        padding-top: 6rem;
        padding-bottom: 8rem;
        min-height: 100vh;
        color: var(--text);
    }

    .trees--footer {
        margin-top: -6rem;
        position: relative;
        z-index: 1;
    }

    .blog-post__header {
        margin-block-end: 3rem;
    }

    .back-link {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        width: fit-content;
        color: var(--text-muted);
        text-decoration: none;
        font-size: 0.875rem;
        margin-block-end: 0.75rem;
        transition: color 0.2s ease;
    }

    .back-link:hover {
        color: var(--text);
    }

    .blog-post__title {
        font-size: clamp(2rem, 5vw, 3.5rem);
        line-height: 1.2;
        margin-block-end: 1rem;
        color: var(--text);
    }

    .blog-post__meta {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.5rem;
        color: var(--text-muted);
        font-size: 0.875rem;
        margin-block-end: 1rem;
    }

    .separator {
        opacity: 0.5;
    }

    .read-time {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
    }

    .read-time svg {
        flex-shrink: 0;
    }

    .author-link {
        color: var(
            --color-dark-blue,
            var(--color-dark-blue-hex, #263eff)
        ) !important;
        text-decoration: none;
        transition: color 0.2s ease;
        font-size: inherit !important;
        font-weight: inherit !important;
    }

    .author-link:hover {
        color: var(--color-blue, var(--color-blue-hex, #00b0ff)) !important;
        text-decoration: underline;
    }

    .interop-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        width: fit-content;
        padding: 0.28rem 0.65rem;
        border-radius: 999px;
        border: 1px solid color-mix(in srgb, #00b0ff 42%, transparent);
        background: linear-gradient(
            135deg,
            rgb(0 176 255 / 0.18),
            rgb(34 211 238 / 0.1)
        );
        color: light-dark(#0b5a77, #dff7ff);
        font-size: 0.68rem;
        font-weight: 800;
        letter-spacing: 0.025em;
        text-transform: uppercase;
        text-decoration: none;
    }

    .interop-badge::before {
        content: "";
        inline-size: 0.4rem;
        block-size: 0.4rem;
        border-radius: 999px;
        background: #22d3ee;
        box-shadow: 0 0 12px rgb(34 211 238 / 0.55);
    }

    .interop-badge--article {
        display: flex;
        margin-block: 0 0.85rem;
    }

    .blog-post__tags {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        margin-block-start: 1rem;
    }

    .tag {
        display: inline-block;
        padding: 0.25rem 0.75rem;
        background: color-mix(in srgb, var(--link, #4a9eff) 20%, transparent);
        color: var(--link, #4a9eff);
        border-radius: 1rem;
        font-size: 0.75rem;
        font-weight: 600;
        border: 1px solid
            color-mix(in srgb, var(--link, #4a9eff) 30%, transparent);
    }

    .tag-link {
        text-decoration: none;
        transition:
            transform 0.2s ease,
            background-color 0.2s ease,
            border-color 0.2s ease;
    }

    .tag-link:hover {
        transform: translateY(-1px);
        background: color-mix(in srgb, var(--link, #4a9eff) 30%, transparent);
        border-color: color-mix(in srgb, var(--link, #4a9eff) 50%, transparent);
    }

    .blog-post__support {
        margin-block-start: 2.5rem;
        padding: 1rem;
        border-radius: 0.9rem;
        border: 1px solid var(--border-color);
        background: var(--bg-elevated);
        position: relative;
        z-index: 20;
    }

    .support-title {
        margin: 0;
        color: var(--text);
        font-size: 1rem;
        font-weight: 700;
    }

    .support-copy {
        margin: 0.5rem 0 0.85rem;
        color: var(--text-muted);
        font-size: 0.88rem;
    }

    .support-copy-note {
        display: block;
        margin-top: 0.35rem;
        color: var(--text-muted);
        font-size: 0.82rem;
    }

    .support-copy code {
        font-size: 0.82em;
    }

    .support-embed-wrap {
        border-radius: 0.75rem;
        overflow: hidden;
        border: 1px solid var(--border-color);
        background: var(--bg-surface);
    }

    .support-embed {
        width: 100%;
        height: 420px;
        min-height: 420px;
        border: 0;
        display: block;
    }

    .support-fallback {
        margin-top: 0.75rem;
        color: var(--text-muted);
        font-size: 0.84rem;
    }

    .support-fallback summary {
        cursor: pointer;
        user-select: none;
        font-weight: 600;
    }

    .support-fallback picture {
        display: block;
        margin-top: 0.7rem;
        border: 1px solid var(--border-color);
        border-radius: 0.6rem;
        overflow: hidden;
        background: var(--bg-surface);
    }

    .support-fallback img {
        display: block;
        width: 100%;
        height: auto;
    }

    .support-source {
        margin: 0.8rem 0 0;
        color: var(--text-muted);
        font-size: 0.8rem;
    }

    .support-source a {
        color: light-dark(#1d4ed8, #6db3f8);
        text-decoration: underline;
        text-underline-offset: 0.15em;
    }

    .blog-post__tag-filters {
        margin-block-start: 3rem;
        padding: 1rem;
        border-radius: 0.9rem;
        border: 1px solid var(--border-color);
        background: var(--bg-elevated);
        position: relative;
        z-index: 20;
    }

    .tag-filters__title {
        margin: 0 0 0.8rem;
        color: var(--text);
        font-size: 0.95rem;
        font-weight: 700;
    }

    .filter-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
    }

    .filter-tag {
        text-decoration: none;
        color: light-dark(#1e3a8a, #93b4f5);
        border: 1px solid var(--border-color);
        background: var(--bg-surface);
        border-radius: 999px;
        padding: 0.35rem 0.7rem;
        font-size: 0.78rem;
        font-weight: 600;
        transition:
            background-color 0.2s ease,
            color 0.2s ease,
            border-color 0.2s ease;
    }

    .filter-tag:hover {
        background: #1d4ed8;
        color: #ffffff;
        border-color: #1d4ed8;
    }

    .blog-post__image {
        margin-block-end: 3rem;
        border-radius: 1rem;
        overflow: hidden;
        background: var(--bg-elevated);
    }

    .blog-post__image img {
        width: 100%;
        height: auto;
        display: block;
    }

    .blog-post__content {
        line-height: 1.7;
        color: var(--text);
    }

    .blog-post__content :global(p),
    .blog-post__content :global(li),
    .blog-post__content :global(span) {
        color: var(--text);
    }

    .blog-post__content :global(ul),
    .blog-post__content :global(ol) {
        margin-bottom: 1rem;
    }

    .blog-post__content :global(h1),
    .blog-post__content :global(h2),
    .blog-post__content :global(h3),
    .blog-post__content :global(h4),
    .blog-post__content :global(h5),
    .blog-post__content :global(h6) {
        color: var(--text);
        font-weight: 700;
    }

    .blog-post__content :global(strong) {
        color: var(--text);
        font-weight: 700;
    }

    .blog-post__content :global(a) {
        color: light-dark(#0066cc, #6db3f8);
        transition: color 0.15s ease;
    }

    .blog-post__content :global(a:hover) {
        color: light-dark(
            oklch(from #0066cc calc(l - 0.12) c h),
            oklch(from #6db3f8 calc(l + 0.1) c h)
        );
    }

    .blog-post__content :global(code) {
        color: #e06c75 !important;
        background: #282c34 !important;
        padding: 0.2em 0.4em;
        border-radius: 0.25rem;
        font-size: 0.875em;
        font-family: "Monaspace Neon", "Fira Code", "Consolas", monospace;
    }

    .blog-post__content :global(pre) {
        position: relative;
        background: #0d1117 !important;
        padding: 1.5rem !important;
        border-radius: 0.75rem !important;
        overflow-x: auto;
        margin-block: 2rem !important;
        border: 1px solid #30363d !important;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    }

    .blog-post__content :global(pre code) {
        background: transparent !important;
        padding: 0 !important;
        font-size: 0.875rem;
        line-height: 1.7;
        color: #e6edf3 !important;
    }

    /* Ensure all code block text is light colored and readable */
    .blog-post__content :global(pre *) {
        color: #e6edf3 !important;
    }

    .blog-post__content :global(pre span) {
        color: #e6edf3 !important;
    }

    .blog-post__footer {
        margin-block-start: 4rem;
        padding-block-start: 2rem;
        border-block-start: 1px solid var(--border-color);
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 1rem;
        flex-wrap: wrap;
        position: relative;
        z-index: 20;
    }

    .btn-back,
    .btn-next {
        display: inline-block;
        padding: 0.75rem 1.5rem;
        background: var(--bg-elevated);
        color: var(--text);
        text-decoration: none;
        border-radius: 0.5rem;
        transition: background 0.2s ease;
        position: relative;
        z-index: 21;
    }

    .btn-back:hover,
    .btn-next:hover {
        background: var(--border-color);
    }

    .btn-next {
        background: var(--color-blue, var(--color-blue-hex));
        color: #fff;
    }

    .btn-next:hover {
        background: var(--color-dark-blue, var(--color-dark-blue-hex));
    }

    .error-page {
        max-width: 800px;
        margin: 0 auto;
        padding: 4rem 1rem;
        text-align: center;
    }

    .error-page h1 {
        color: var(--text);
        margin-block-end: 2rem;
    }

    .error-page a {
        color: var(--link, #4a9eff);
        text-decoration: none;
    }
</style>
